<template>
  <main role="main" class="py-4">
    <b-container fluid="xl" class="mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("appList") }}</h4>
        <div>
          <b-button variant="outline-secondary" class="mr-4" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button variant="outline-primary" v-b-modal="createModalId">
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between mb-3">
        <div>
          <b-form class="d-inline-block" inline @submit.prevent="onClickSearch">
            <b-form-input class="mr-2" type="search" minlength="2" maxlength="200" v-model="searchText"
                          style="min-width: 300px;" />
            <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
          </b-form>
        </div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(logo)="data">
          <div v-if="data.item.logo" class="logo-container bg-primary mb-2">
            <img :src="data.item.logo" alt="logo">
          </div>
        </template>
        <template v-slot:cell(customerName)="data">
          <router-link :to="`/customers/details/${data.item.customerId}`">{{ data.item.customerName }}</router-link>
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button @click="onClickEditApp(data.item)">{{ $t("editInfo") }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickDeleteApp(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </b-container>

    <b-modal :id="createModalId" :title="$t('createApp')" hide-footer no-close-on-backdrop>
      <create-app-modal :modal-id="createModalId" @submitted="refreshData" />
    </b-modal>

    <b-modal :id="editModalId" :title="$t('updateApp')" hide-footer no-close-on-backdrop>
      <edit-app-modal :item="itemToEdit" :modal-id="editModalId" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>
  .logo-container {
    flex-shrink: 0;
    width: 50px; height: 50px;
    display: grid; place-items: center;

    > img {
      width: 28px;
    }
  }
</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";
  import * as appNamesService from "@/service/appNamesService";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import CreateAppModal from "@/components/app-names/CreateAppModal.vue";
  import NewAppModal from "@/components/app-names/CreateAppModal.vue";
  import EditAppModal from "@/components/app-names/EditAppModal.vue";

  @Component({
    components: {EditAppModal, CreateAppModal, NewAppModal }
  })
  export default class AppNames extends BaseComponent {

    readonly createModalId = "create-app-modal";
    readonly editModalId = "edit-app-modal";

    searchText = "";

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#",
        thClass: "text-nowrap",
      },
      {
        key: "name",
        label: this.$t("appName").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "domainName",
        label: "Domain",
        thClass: "text-nowrap",
      },
      {
        key: "logo",
        label: "Logo",
      },
      {
        key: "customerName",
        label: this.$t("customer").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    itemToEdit = null;


    mounted() {

    }


    onClickReloadBtn() {
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    onClickSearch() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }


    onClickEditApp(item) {
      this.itemToEdit = item;
      this.$bvModal.show(this.editModalId);
    }


    async onClickDeleteApp(user) {
      const modalMessage = this.$t("deleteRecordConfirmation", { name: user.name, id: user.id }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          await appNamesService.del(user.id);
          this.refreshData();
          this.toastSuccessDelay(this.$t("deleteSuccessful").toString());

        } catch (e) {
          this.toastError(e);
        }
      }
    }


    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const res = await appNamesService.getAll({
          page: ctx.currentPage,
          pageSize: this.itemsPerPage,
          q: this.searchText?.trim(),
        });

        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }

  }
</script>
